/**
 * Popin — composant de fenêtre modale standard du RABBIT Design System.
 *
 * Source unique pour toutes les popins de l'app : overlay, portal, gestion
 * clavier (Escape), scroll lock, focus, et surtout un header / body / footer
 * alignés sur la même gouttière (cf. tokens --popin-*).
 *
 * Composition :
 *   <Popin isOpen onClose title="..." footer={<PopinFooter>...</PopinFooter>}>
 *     contenu
 *   </Popin>
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Popin.css';

export type PopinSize = 'sm' | 'md' | 'lg';

export interface PopinProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: PopinSize;
  /** Fermer au clic sur l'overlay (défaut: true) */
  closeOnOverlayClick?: boolean;
  /** Fermer avec la touche Escape (défaut: true) */
  closeOnEscape?: boolean;
  /** Afficher la croix de fermeture (défaut: true) */
  showCloseButton?: boolean;
  className?: string;
}

export const Popin: React.FC<PopinProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  className = '',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  // onClose via ref : l'effet d'ouverture ne doit PAS se relancer quand le
  // parent re-rend avec une nouvelle identité de callback (sinon le focus du
  // dialog est ré-appliqué à chaque frappe dans un champ — l'input perd le
  // focus après une seule lettre).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Escape + scroll lock + restauration du focus — à l'ouverture uniquement.
  useEffect(() => {
    if (!isOpen) return;

    previousActiveElement.current = document.activeElement as HTMLElement;
    dialogRef.current?.focus();
    document.body.style.overflow = 'hidden';
    // Compensation de la scrollbar masquée : sans ce padding, le viewport
    // s'élargit d'un coup à l'ouverture et tout le layout ancré à droite
    // (ex. panneau BlockFullscreen) saute horizontalement en pleine
    // animation d'entrée — effet « rebond ».
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('keydown', handleEscape);
      previousActiveElement.current?.focus();
    };
  }, [isOpen, closeOnEscape]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const content = (
    <div className="ds-popin__overlay" onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        className={`ds-popin ds-popin--${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        {(title || showCloseButton) && (
          <header className="ds-popin__header">
            {title ? <h2 className="ds-popin__title">{title}</h2> : <span />}
            {showCloseButton && (
              <button
                type="button"
                className="ds-popin__close"
                onClick={onClose}
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </header>
        )}

        <div className="ds-popin__body">{children}</div>

        {footer && <footer className="ds-popin__footer">{footer}</footer>}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

/** Conteneur d'actions standard pour le footer (aligné à droite). */
export const PopinFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => <div className={`ds-popin__actions ${className}`.trim()}>{children}</div>;

export default Popin;
